import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";
import Select from "react-select";
import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import TanstackTable from "../../components/Common/TanstackTable";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const PaymentAccounts = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Payment Accounts", link: "#" },
  ]);

  const [data, setData] = useState([]);
  const [methods, setMethods] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    method_id: "",
  });

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const result = await apiClient.get("/payment/payment-accounts/");
        setData(result.data.response || result.data || []);
      } catch (err) {
        console.error("Error fetching payment accounts:", err);
      }
    };
    const fetchMethods = async () => {
      try {
        const result = await apiClient.get("/payment/payment-methods");
        setMethods(result.data.response || result.data || []);
      } catch (err) {
        console.error("Error fetching payment methods:", err);
      }
    };
    fetchAccounts();
    fetchMethods();
  }, [modalOpen]);

  const methodOptions = [
    { label: "Select Method", value: "" },
    ...methods.map((m) => ({
      label: m.name,
      value: m.id,
    })),
  ];

  const toggleModal = () => {
    setFormData({
      id: null,
      name: "",
      method_id: "",
    });
    setModalOpen(!modalOpen);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (formData.id) {
        response = await apiClient.put(
          `/payment/payment-accounts/${formData.id}`,
          {
            name: formData.name,
            method_id: formData.method_id,
          }
        );
      } else {
        response = await apiClient.post("/payment/payment-accounts/", {
          name: formData.name,
          method_id: formData.method_id,
        });
      }
      await showAlert({
        title: formData.id
          ? "Update Payment Account"
          : "Create Payment Account",
        text: response.data.message,
        icon: response.data.status,
        goBack: false,
        timer: 5000,
      });
      if (response.data.status === "success") {
        setModalOpen(false);
      }
    } catch (err) {
      console.error("Error saving payment account:", err);
    }
  };

  const columns = [
    {
      id: "index",
      header: "No",
      cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "account",
      header: "Account Name",
    },
    {
      accessorKey: "method",
      header: "Payment Method",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`badge bg-${
            row.original.status === "Active"
              ? "success"
              : row.original.status === "Inactive"
              ? "secondary"
              : "danger"
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => {
            setFormData({
              id: row.original.id,
              name: row.original.account,
              method_id:
                methods.find((m) => m.name === row.original.method)?.id || "",
            });
            setModalOpen(true);
          }}
        >
          <i className="ri-file-edit-line"></i>
        </button>
      ),
    },
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#007bff"
        : state.isFocused
        ? "#f0f0f0"
        : "white",
      color: state.isSelected ? "white" : "black",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs
              title="Payment Accounts"
              breadcrumbItems={breadcrumbItems}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <Card>
              <CardBody>
                <div className="row">
                  <div className="col-12 text-end mb-2">
                    <button className="btn btn-primary" onClick={toggleModal}>
                      Create Payment Account
                    </button>
                  </div>
                </div>
                <TanstackTable data={data} columns={columns} />
              </CardBody>
            </Card>
          </div>
        </div>
        <Modal
          isOpen={modalOpen}
          toggle={toggleModal}
          centered={true}
          backdrop="static"
        >
          <form onSubmit={handleSave}>
            <ModalHeader toggle={toggleModal}>
              {formData.id ? "Update" : "Create"} Payment Account
            </ModalHeader>
            <ModalBody>
              <div className="mb-3">
                <Label className="form-label">Account Name</Label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Account Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="mb-3">
                <Label className="form-label">Payment Method</Label>
                <Select
                  name="method_id"
                  value={methodOptions.find(
                    (option) => option.value === formData.method_id
                  )}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      method_id: selected ? selected.value : "",
                    }))
                  }
                  options={methodOptions}
                  styles={customStyles}
                  classNamePrefix="select2-selection form-control"
                  required
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <button
                className="btn btn-secondary"
                onClick={toggleModal}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={!formData.name.trim() || !formData.method_id}
              >
                Save
              </button>
            </ModalFooter>
          </form>
        </Modal>
      </Container>
    </div>
  );
};

export default PaymentAccounts;
